// eslint-disable-next-line no-unused-vars
const getTheme = (mode = 'light') => {
  const isLight = mode === 'light';

  // ── Biodimension Design System tokens ──────────────────────────────────
  // Light palette
  const L = {
    bg:                   '#f6f4ef',
    fg:                   '#1d2321',
    muted:                '#5f6b66',
    faint:                '#8a948f',
    surface:              '#fffefb',
    surface2:             '#efece4',
    surface3:             '#e7e3d9',
    outline:              '#e4ded2',
    outlineStrong:        '#d3ccbd',
    primary:              '#0e7c8c',
    onPrimary:            '#ffffff',
    primaryContainer:     '#d3e9ec',
    onPrimaryContainer:   '#06363f',
    accent:               '#b95b41',
    onAccent:             '#ffffff',
    accentContainer:      '#f5ddd2',
    onAccentContainer:    '#5a2417',
  };

  // Dark palette
  const D = {
    bg:                   '#101413',
    fg:                   '#e8ecea',
    muted:                '#9aa6a1',
    faint:                '#6f7b76',
    surface:              '#181d1c',
    surface2:             '#1e2523',
    surface3:             '#242c2a',
    outline:              '#2a3331',
    outlineStrong:        '#394441',
    primary:              '#62c7d3',
    onPrimary:            '#00363f',
    primaryContainer:     '#134950',
    onPrimaryContainer:   '#b7ebf1',
    accent:               '#e89c83',
    onAccent:             '#4a1b0f',
    accentContainer:      '#5a2a1c',
    onAccentContainer:    '#f8ddd2',
  };

  const t = isLight ? L : D;

  // Warm shadows — never pure black
  const shadow1 = isLight
    ? '0 1px 2px rgba(29,35,33,.04), 0 4px 12px -6px rgba(29,35,33,.10)'
    : '0 1px 2px rgba(0,0,0,.12), 0 4px 12px -6px rgba(0,0,0,.28)';
  const shadow2 = isLight
    ? '0 2px 6px rgba(29,35,33,.05), 0 18px 40px -20px rgba(29,35,33,.18)'
    : '0 2px 6px rgba(0,0,0,.14), 0 18px 40px -20px rgba(0,0,0,.42)';
  const shadow3 = isLight
    ? '0 6px 16px rgba(29,35,33,.07), 0 36px 64px -28px rgba(29,35,33,.24)'
    : '0 6px 16px rgba(0,0,0,.18), 0 36px 64px -28px rgba(0,0,0,.52)';

  return {
    palette: {
      mode,
      primary: {
        main:        t.primary,
        light:       isLight ? '#4aabb9' : '#8dd9e1',
        dark:        isLight ? '#075e6b' : '#3da8b5',
        contrastText: t.onPrimary,
      },
      secondary: {
        main:        t.accent,
        light:       isLight ? '#d07b63' : '#eebbaa',
        dark:        isLight ? '#8c3e2c' : '#c26e55',
        contrastText: t.onAccent,
      },
      error: {
        main:        isLight ? '#c0392b' : '#e57373',
        light:       isLight ? '#e53935' : '#ef9a9a',
        dark:        isLight ? '#922b21' : '#c62828',
        contrastText: '#ffffff',
      },
      warning: {
        main:        isLight ? '#d97706' : '#fbbf24',
        contrastText: '#ffffff',
      },
      success: {
        main:        isLight ? '#4a7c59' : '#6db97f',
        contrastText: '#ffffff',
      },
      info: {
        main:        t.primary,
        contrastText: t.onPrimary,
      },
      background: {
        default: t.bg,
        paper:   t.surface,
      },
      text: {
        primary:   t.fg,
        secondary: t.muted,
        disabled:  t.faint,
      },
      divider: t.outline,
      // Extra tokens exposed as theme.palette.brand.*
      brand: {
        teal:             '#0e7c8c',
        tealSoft:         '#cfe6e9',
        clay:             '#c2674f',
        claySoft:         '#f2ddd2',
        sage:             '#6f9e5a',
        surface2:         t.surface2,
        surface3:         t.surface3,
        outline:          t.outline,
        outlineStrong:    t.outlineStrong,
        primaryContainer: t.primaryContainer,
        onPrimaryContainer: t.onPrimaryContainer,
        accentContainer:  t.accentContainer,
        onAccentContainer: t.onAccentContainer,
      },
    },
    typography: {
      fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
      h1: { fontWeight: 800, fontSize: '2.5rem',  letterSpacing: '-0.025em' },
      h2: { fontWeight: 700, fontSize: '2rem',    letterSpacing: '-0.025em' },
      h3: { fontWeight: 700, fontSize: '1.75rem', letterSpacing: '-0.02em' },
      h4: { fontWeight: 600, fontSize: '1.5rem',  letterSpacing: '-0.015em' },
      h5: { fontWeight: 600, fontSize: '1.25rem' },
      h6: { fontWeight: 600, fontSize: '1rem' },
      body1:   { fontSize: '1rem',    lineHeight: 1.5 },
      body2:   { fontSize: '0.875rem', lineHeight: 1.5 },
      caption: { fontSize: '0.75rem', lineHeight: 1.66, letterSpacing: '0.02em' },
      button:  { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
      overline:{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.7rem', fontWeight: 700 },
    },
    shape: {
      // --radius-sm ≈ 0.875rem = 14px
      borderRadius: 14,
    },
    shadows: [
      'none',
      shadow1,
      shadow2,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
      shadow3,
    ],
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            // --radius-sm
            borderRadius: 14,
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 20px',
            boxShadow: shadow1,
            '&:hover': { boxShadow: shadow2 },
          },
          containedPrimary: {
            background: t.primary,
            color: t.onPrimary,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 22, // --radius-2xl ≈ 2.75rem = 44px/2
            border: `1px solid ${t.outline}`,
            boxShadow: shadow2,
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: { padding: '16px 24px' },
          title: { fontSize: '1.1rem', fontWeight: 700 },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: '16px 24px',
            '&:last-child': { paddingBottom: 16 },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: t.surface,
            border: `1px solid ${t.outline}`,
          },
          elevation1: { boxShadow: shadow1 },
          elevation2: { boxShadow: shadow2 },
          elevation3: { boxShadow: shadow3 },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 14,
              backgroundColor: t.surface3,
              '&:hover': { backgroundColor: t.surface2 },
              '&.Mui-focused': { backgroundColor: t.surface },
              '& fieldset': { borderColor: t.outline },
              '&:hover fieldset': { borderColor: t.outlineStrong },
              '&.Mui-focused fieldset': { borderColor: t.primary, borderWidth: 1.5 },
            },
            '& .MuiInputLabel-root': { color: t.muted },
            '& .MuiInputLabel-root.Mui-focused': { color: t.primary },
            '& .MuiInputBase-input': { color: t.fg },
            '& .MuiFormHelperText-root': { color: t.muted },
          },
        },
      },
      MuiDialog: {
        defaultProps: { disableScrollLock: true },
        styleOverrides: {
          paper: {
            borderRadius: 20,
            backgroundImage: 'none',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${t.outline}`,
            color: t.fg,
            fontWeight: 700,
            fontSize: '1.1rem',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: { root: { color: t.fg } },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: { borderTop: `1px solid ${t.outline}` },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            border: `1px solid ${t.outline}`,
            overflow: 'hidden',
            '& .MuiTable-root': { borderCollapse: 'separate', borderSpacing: 0 },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontWeight: 700,
              backgroundColor: t.surface2,
              backdropFilter: 'blur(8px)',
              color: t.fg,
              borderBottom: `2px solid ${t.outlineStrong}`,
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:last-child td': { borderBottom: 0 },
            '&:hover': {
              backgroundColor: isLight
                ? 'rgba(14,124,140,0.04)'
                : 'rgba(98,199,211,0.04)',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${t.outline}`,
            color: t.fg,
            '&:first-of-type': { paddingLeft: '24px' },
            '&:last-child': { paddingRight: '24px' },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 48 },
          indicator: {
            height: 3,
            borderRadius: '3px 3px 0 0',
            backgroundColor: t.primary,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            color: t.muted,
            '&.Mui-selected': { color: t.fg },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 600 },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            backgroundColor: isLight
              ? 'rgba(14,124,140,0.06)'
              : 'rgba(98,199,211,0.06)',
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: { borderRadius: 20 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: isLight
                ? 'rgba(14,124,140,0.06)'
                : 'rgba(98,199,211,0.06)',
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'thin',
            scrollbarColor: `${t.outlineStrong} ${t.surface2}`,
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              width: 6,
              height: 6,
              backgroundColor: t.surface2,
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              backgroundColor: t.outlineStrong,
              borderRadius: 99,
            },
            '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
              backgroundColor: t.muted,
            },
          },
        },
      },
    },
  };
};

export default getTheme;